import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Active traps, last opened last. With the focus outside every trap (body,
 * after a tap on the court), only the last one takes Tab: two dialogs stacked
 * as siblings (edit over point end) both listen on the document, and the one
 * underneath, registered first, used to win.
 */
const openTraps: Array<RefObject<HTMLElement | null>> = [];

/**
 * Element outside the dialog that stays reachable with Tab while a trap is
 * open: the capture video docked beside the lineup of the next set (VOL-336,
 * VOL-208), whose player is used to read the starting positions.
 */
export const FOCUS_TRAP_COMPANION_ATTR = 'data-focus-trap-companion';

function companionElements(): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(`[${FOCUS_TRAP_COMPANION_ATTR}]`));
}

/**
 * `display: none` (on the node or an ancestor inside its scope) or `visibility: hidden`:
 * `focus()` is a no-op there, so the companion's manual cycling would stop on it for good.
 */
function isRendered(node: HTMLElement, scope: HTMLElement): boolean {
  if (getComputedStyle(node).visibility === 'hidden') return false;
  for (let el: HTMLElement | null = node; el; el = el.parentElement) {
    if (getComputedStyle(el).display === 'none') return false;
    if (el === scope) break;
  }
  return true;
}

function inDocumentOrder(nodes: HTMLElement[]): HTMLElement[] {
  return nodes.sort((a, b) =>
    a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
  );
}

interface UseFocusTrapOptions {
  enabled: boolean;
  restoreFocus?: boolean;
  /**
   * `container` focuses the dialog itself, not its first button: in live
   * capture, Enter on a pre-focused button would validate a choice by accident.
   */
  initialFocus?: 'first' | 'container';
}

interface UseFocusTrapReturn<T extends HTMLElement> {
  containerRef: RefObject<T | null>;
}

export function useFocusTrap<T extends HTMLElement = HTMLDivElement>({
  enabled,
  restoreFocus = true,
  initialFocus = 'first',
}: UseFocusTrapOptions): UseFocusTrapReturn<T> {
  const containerRef = useRef<T | null>(null);
  // Read through refs: a change while the dialog is open must not tear the trap
  // down and set it up again (it would move the focus and reorder `openTraps`).
  const restoreFocusRef = useRef(restoreFocus);
  restoreFocusRef.current = restoreFocus;
  const initialFocusRef = useRef(initialFocus);
  initialFocusRef.current = initialFocus;

  useEffect(() => {
    if (!enabled) return;

    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // A child that grabbed the focus itself (autoFocus) keeps it.
    if (!container.contains(document.activeElement)) {
      const first =
        initialFocusRef.current === 'first'
          ? container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
          : null;
      if (first) {
        first.focus();
      } else {
        container.setAttribute('tabindex', '-1');
        container.focus({ preventScroll: true });
      }
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return;

      const current = containerRef.current;
      if (!current) return;

      const companions = companionElements().filter(
        (el) => !el.contains(current) && !current.contains(el),
      );
      const scopes = [current, ...companions];
      const inTrap = (el: Element | null): boolean =>
        el !== null && scopes.some((scope) => scope.contains(el));

      const nodes = inDocumentOrder(
        scopes.flatMap((scope) =>
          Array.from(scope.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((node) => isRendered(node, scope)),
        ),
      );
      if (nodes.length === 0) {
        event.preventDefault();
        return;
      }

      const firstNode = nodes[0];
      const lastNode = nodes[nodes.length - 1];
      const active = document.activeElement;

      // A modal stacked on this one (setter picker over the lineup) owns Tab.
      const activeModal = active instanceof HTMLElement ? active.closest('[aria-modal="true"]') : null;
      if (activeModal && activeModal !== current && !activeModal.contains(current)) return;
      if (!inTrap(active) && openTraps[openTraps.length - 1] !== containerRef) return;

      if (companions.length > 0) {
        // The companion is not next to the dialog in the DOM: the browser would
        // tab from it to the capture screen under the backdrop.
        event.preventDefault();
        const index = active instanceof HTMLElement ? nodes.indexOf(active) : -1;
        const target =
          index === -1
            ? event.shiftKey ? lastNode : firstNode
            : nodes[(index + (event.shiftKey ? -1 : 1) + nodes.length) % nodes.length];
        target.focus();
        return;
      }

      if (event.shiftKey) {
        // The container itself can hold the focus (capture shortcuts move it there
        // after a tap, VOL-275): Shift+Tab from it would leave the trap.
        if (active === firstNode || active === current || !current.contains(active)) {
          event.preventDefault();
          lastNode.focus();
        }
      } else if (active === lastNode || !current.contains(active)) {
        event.preventDefault();
        firstNode.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // The ref, not the element: the element carrying it can change while open
    // (conditional card of a capture dialog).
    openTraps.push(containerRef);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const index = openTraps.lastIndexOf(containerRef);
      if (index !== -1) openTraps.splice(index, 1);
      if (restoreFocusRef.current && previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [enabled]);

  return { containerRef };
}
